/**
 * Which route into a meal this user reaches for most, and how the "Add to this meal"
 * routes are ordered because of it (mh-physique#4969).
 *
 * The meal screen offers several ways to get food into a meal. Their order was measured
 * once, in Mixpanel on 26 August 2026, across every user -- but which route a PARTICULAR
 * person uses is a different question, and the folded strip only has room to show the
 * first two or three of them. So the order adapts: a route this user takes often moves up,
 * and one they never take drifts down.
 *
 * **How the score works.** Every route holds one number. On each use, every score is
 * multiplied by 0.9 and the route that was used then gains 1. That is decay per USE, not
 * per elapsed day, which is what the product owner asked for: somebody who logs food twice
 * a year is not quietly reset to the seeded order between visits, because nothing happens
 * while they are away.
 *
 * The numbers cannot run away. Using one route over and over gives it 1 + 0.9 + 0.9*0.9 +
 * ..., which converges on 1 / (1 - 0.9) = 10, so 10 is the ceiling and MAX_ROUTE_SCORE
 * clamps anything a damaged document might hold to it. A route that is never used is
 * multiplied by 0.9 again on every other route's use and heads for zero.
 *
 * **Seeding.** A fresh account has no scores at all, and starting everything at zero would
 * make the first tap of any route throw it to the top. So a route with no stored score
 * counts as SEED_TOP minus its position times SEED_STEP (3, 2.5, 2, ..., floored at 0),
 * which reproduces the measured order exactly for a new user and takes about two uses to
 * overtake the route above it. Worked through: a route seeded 0 that is used twice ends on
 * 1.9, while a route seeded 2 that is not used has decayed to 1.62.
 *
 * **The first two positions never move.** Add Foods is the route the whole screen exists
 * for, and Scan Barcode is the one a user could not find before leaving for another app
 * (mh-physique#3888) -- it must never become an unlabelled glyph behind the overflow button
 * on an account that has not used it yet. Everything below them is ordered by score.
 *
 * Kept as plain functions in their own file, with no Angular and no database, so the rules
 * above can be tested directly -- the same reason `copy-meal-ingredients.ts` next door is.
 */

/**
 * Every route into a meal, in the order measured in Mixpanel on 26 August 2026.
 *
 * This one list is canonical for three separate things: the order a brand-new account
 * sees, the element id of each row (so an end-to-end test can address one), and the set of
 * keys allowed in the stored scores. A route that is switched off by config is still listed
 * here -- it is simply not drawn -- so its score survives the feature being turned off and
 * on again.
 */
export const ADD_TO_MEAL_ROUTE_IDS = [
  'addFoodsRow',
  'scanBarcodeRow',
  'scanLabelRow',
  'logMacrosManuallyRow',
  'scanMealRow',
  'autoSuggestionsRow',
  'copyFromDayRow',
] as const;

export type AddToMealRouteId = typeof ADD_TO_MEAL_ROUTE_IDS[number];

/** How many routes keep a fixed position at the front, whatever the scores say. */
export const PINNED_ROUTE_COUNT = 2;

/** What every score is multiplied by each time any route is used. */
export const ROUTE_USE_DECAY = 0.9;

/** The highest a score can ever reach: 1 / (1 - ROUTE_USE_DECAY). */
export const MAX_ROUTE_SCORE = 10;

/** The pretend score of the first route on an account that has never used any of them. */
const SEED_TOP = 3;

/** How much less each following route is seeded with. */
const SEED_STEP = 0.5;

/** Scores are stored as JSON, so they are rounded to keep that text short. */
const STORED_DECIMALS = 4;

/** A score per route id. A route with no entry has never been used on this account. */
export type RouteUsageScores = Record<string, number>;

/**
 * The score a route counts as when nothing has been stored for it yet.
 *
 * Positions come from `ADD_TO_MEAL_ROUTE_IDS`, not from whatever subset is being drawn, so
 * turning a config-gated route off does not shift the seeds of the routes below it.
 */
function seedScore(routeId: string): number {
  const position = (ADD_TO_MEAL_ROUTE_IDS as readonly string[]).indexOf(routeId);
  // a route we do not know about is not seeded at all, so it sorts to the bottom
  if (position < 0) return 0;
  return Math.max(0, SEED_TOP - position * SEED_STEP);
}

/** the stored score of one route, or its seed when nothing usable is stored */
function effectiveScore(scores: RouteUsageScores, routeId: string): number {
  const stored = scores[routeId];
  if (typeof stored === 'number' && Number.isFinite(stored)) {
    return Math.min(Math.max(stored, 0), MAX_ROUTE_SCORE);
  }
  return seedScore(routeId);
}

/**
 * Reads the stored scores out of the settings field, discarding anything unusable.
 *
 * This document syncs between devices and can be years old, so every part of it is treated
 * as untrusted: text that is not JSON, JSON that is not a plain object, a key naming a
 * route that no longer exists, and a value that is not a finite number are each dropped on
 * their own. A dropped route falls back to its seed, so a damaged document costs the user
 * their personal ordering and nothing else -- it can neither throw nor reorder wrongly.
 */
export function readRouteUsage(
  storedJson: string | undefined,
  reportUnreadable?: (err: unknown) => void,
): RouteUsageScores {
  if (!storedJson) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(storedJson);
  } catch (err) {
    // The screen carries on with the seeded order, so the user loses nothing they would
    // notice. Somebody still needs to know: nothing but this code writes this field, so text
    // that will not parse means something else damaged it, and that would go on happening.
    // Handing it to the caller keeps this module free of Angular and of the logging service.
    if (reportUnreadable) reportUnreadable(err);
    return {};
  }

  // text that parses but is not an object of scores was damaged by the same something-else as
  // text that does not parse at all, so it is reported for the same reason. A known route
  // holding an unusable VALUE is not reported below: that is also how a document written by a
  // future version would look to this one, and the seeded fallback handles it either way.
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    if (reportUnreadable) reportUnreadable(new Error(`stored usage is ${describeShape(parsed)}, not an object of scores`));
    return {};
  }

  const scores: RouteUsageScores = {};
  for (const routeId of ADD_TO_MEAL_ROUTE_IDS) {
    const value = (parsed as Record<string, unknown>)[routeId];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    scores[routeId] = Math.min(Math.max(value, 0), MAX_ROUTE_SCORE);
  }
  return scores;
}

/**
 * The scores after one use of `usedRouteId`: everything decays, the used route gains 1.
 *
 * Returns a new object and never changes the one it was given, so the caller can store the
 * answer without worrying about what else holds the old scores.
 */
export function recordRouteUse(scores: RouteUsageScores, usedRouteId: string): RouteUsageScores {
  // a route we do not know about cannot be scored, and inventing a key for it would let the
  // stored document grow without limit
  if (!(ADD_TO_MEAL_ROUTE_IDS as readonly string[]).includes(usedRouteId)) return { ...scores };

  const updated: RouteUsageScores = {};
  for (const routeId of ADD_TO_MEAL_ROUTE_IDS) {
    let score = effectiveScore(scores, routeId) * ROUTE_USE_DECAY;
    if (routeId === usedRouteId) score += 1;
    updated[routeId] = roundForStorage(Math.min(score, MAX_ROUTE_SCORE));
  }
  return updated;
}

/** what the stored text turned out to be, for a report nobody can attach a debugger to */
function describeShape(parsed: unknown): string {
  if (parsed === null) return 'null';
  if (Array.isArray(parsed)) return 'an array';
  return `a ${typeof parsed}`;
}

function roundForStorage(value: number): number {
  const factor = Math.pow(10, STORED_DECIMALS);
  return Math.round(value * factor) / factor;
}

/**
 * Orders the routes for display: the pinned ones first, then the rest by score.
 *
 * The rows handed in are in the canonical order, and ties are broken by that order, so the
 * answer is stable -- two routes on the same score never swap places between two renders of
 * the same screen.
 */
export function orderRoutesByUsage<T extends { id: string }>(
  rows: readonly T[],
  scores: RouteUsageScores,
): T[] {
  const pinned = rows.slice(0, PINNED_ROUTE_COUNT);
  const rest = rows.slice(PINNED_ROUTE_COUNT);

  const sorted = rest
    .map((row, canonicalIndex) => ({ row, canonicalIndex }))
    .sort((a, b) => {
      const difference = effectiveScore(scores, b.row.id) - effectiveScore(scores, a.row.id);
      if (difference !== 0) return difference;
      return a.canonicalIndex - b.canonicalIndex;
    })
    .map(entry => entry.row);

  return [...pinned, ...sorted];
}
